import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { RequireAuth } from '../auth/RequireAuth';
import { AddBlockModal } from '../components/editor/AddBlockModal';
import { BlockList } from '../components/editor/BlockList';
import {
  getUserProfile,
  createBlock,
  updateBlock,
  deleteBlock,
  onBlocksChange,
} from '../lib/firestore';
import { uploadPostImage } from '../lib/storage';
import type { Block, BlockType } from '../lib/types';

function EditorPageContent() {
  const { user, signOut, isDemoUser } = useAuth();
  const navigate = useNavigate();

  // State
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Load user data and blocks
  useEffect(() => {
    if (!user) return;

    // For demo user, use mock data
    if (isDemoUser) {
      setUsername('demo_cat');
      setBlocks([
        {
          id: 'demo-1',
          type: 'link',
          title: 'My Website',
          url: 'https://example.com',
          order: 0,
          enabled: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: 'demo-2',
          type: 'post',
          title: 'Latest Artwork',
          content: 'Check out my new digital painting!',
          imageUrl: '/img/profile_intp.png',
          order: 1,
          enabled: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ]);
      setLoading(false);
      return;
    }

    // Load real data
    const loadProfile = async () => {
      try {
        const profile = await getUserProfile(user.uid);
        setUsername(profile?.username || null);
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };

    loadProfile();

    // Subscribe to blocks changes
    const unsubscribe = onBlocksChange(user.uid, (newBlocks) => {
      setBlocks(newBlocks);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, isDemoUser]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Failed to sign out:', err);
    }
  };

  const handlePreview = () => {
    if (username) {
      // Open in new tab
      window.open(`/${username}`, '_blank');
    } else {
      alert('Please set up your username in Settings first');
      navigate('/settings');
    }
  };

  const handleAddBlock = async (blockData: {
    type: BlockType;
    title: string;
    url?: string;
    content?: string;
    imageFile?: File;
  }) => {
    if (!user) return;

    if (isDemoUser) {
      // Demo mode: just add to local state
      const newBlock: Block = {
        id: `demo-${Date.now()}`,
        type: blockData.type,
        title: blockData.title,
        url: blockData.url,
        content: blockData.content,
        imageUrl: blockData.imageFile ? URL.createObjectURL(blockData.imageFile) : undefined,
        order: blocks.length,
        enabled: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      setBlocks([...blocks, newBlock]);
      return;
    }

    // Upload image if provided
    let imageUrl: string | undefined;
    if (blockData.imageFile) {
      imageUrl = await uploadPostImage(user.uid, blockData.imageFile);
    }

    // Create block in Firestore
    await createBlock(user.uid, {
      type: blockData.type,
      title: blockData.title,
      url: blockData.url,
      content: blockData.content,
      imageUrl,
      order: blocks.length,
      enabled: true,
    });
  };

  const handleEditBlock = (_block: Block) => {
    // TODO: Implement edit modal
    alert('Edit functionality coming soon!');
  };

  const handleDeleteBlock = async (blockId: string) => {
    if (!user) return;

    if (isDemoUser) {
      setBlocks(blocks.filter((b) => b.id !== blockId));
      return;
    }

    await deleteBlock(user.uid, blockId);
  };

  const handleToggleEnabled = async (blockId: string, enabled: boolean) => {
    if (!user) return;

    if (isDemoUser) {
      setBlocks(
        blocks.map((b) => (b.id === blockId ? { ...b, enabled } : b))
      );
      return;
    }

    await updateBlock(user.uid, blockId, { enabled });
  };

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-b from-pink-50 to-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gradient">Editor</h1>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              Settings
            </Button>
            <Button variant="primary" onClick={handlePreview}>
              Preview
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Demo Mode Banner */}
        {isDemoUser && (
          <div className="bg-yellow-100 border-2 border-yellow-400 text-yellow-800 px-4 py-3 rounded-xl mb-6">
            <strong>Demo Mode:</strong> Changes are not saved to the server.
          </div>
        )}

        {/* Blocks Section */}
        <div className="card-pop mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Your Blocks</h2>
            <span className="text-sm text-gray-500">
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-2xl animate-bounce">⏳</div>
              <p className="text-gray-500 mt-2">Loading blocks...</p>
            </div>
          ) : (
            <BlockList
              blocks={blocks}
              onEdit={handleEditBlock}
              onDelete={handleDeleteBlock}
              onToggleEnabled={handleToggleEnabled}
            />
          )}
        </div>

        {/* Add Block Button */}
        <div
          onClick={() => setIsAddModalOpen(true)}
          className="card-pop border-2 border-dashed border-gray-300 flex items-center justify-center min-h-[120px] cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center gap-2 text-gray-500 group-hover:text-primary">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="font-semibold text-lg">Add New Block</span>
          </div>
        </div>
      </div>

      {/* Add Block Modal */}
      <AddBlockModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddBlock}
      />
    </div>
  );
}

export function EditorPage() {
  return (
    <RequireAuth>
      <EditorPageContent />
    </RequireAuth>
  );
}
